import React, { useState } from 'react';

interface Rating {
  rating: number;
  userCount: number;
  breakdown: {
    5: number;
    4: number;
    3: number;
    2: number;
    1: number;
  };
}

interface Review {
  id: string;
  userName: string;
  userAvatar?: string;
  rating: number;
  date: string;
  content: string;
  likes: number;
  hasLiked?: boolean;
  replies?: Reply[];
}

interface Reply {
  id: string;
  userName: string;
  userAvatar?: string;
  date: string;
  content: string;
  isAuthor?: boolean;
}

interface UserFeedbackProps {
  bookId: number;
  bookTitle: string;
  ratings: Rating;
  reviews: Review[];
  userHasReviewed?: boolean;
  onAddReview: (bookId: number, rating: number, content: string) => void;
  onLikeReview: (reviewId: string) => void;
  onAddReply: (reviewId: string, content: string) => void;
}

const UserFeedback: React.FC<UserFeedbackProps> = ({
  bookId,
  bookTitle,
  ratings,
  reviews,
  userHasReviewed = false,
  onAddReview,
  onLikeReview,
  onAddReply
}) => {
  const [userRating, setUserRating] = useState(0);
  const [reviewContent, setReviewContent] = useState('');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState<Record<string, boolean>>({});
  const [replyContents, setReplyContents] = useState<Record<string, string>>({});

  const handleRatingChange = (rating: number) => {
    setUserRating(rating);
  };

  const handleSubmitReview = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (userRating === 0) {
      // Arată eroare pentru rating lipsă
      return;
    }
    
    if (reviewContent.trim().length < 10) {
      // Arată eroare pentru recenzie prea scurtă
      return;
    }
    
    onAddReview(bookId, userRating, reviewContent);
    setUserRating(0);
    setReviewContent('');
    setShowReviewForm(false);
  };

  const toggleReviewExpand = (reviewId: string) => {
    setExpandedReviews({
      ...expandedReviews,
      [reviewId]: !expandedReviews[reviewId]
    });
  };

  const handleReplyChange = (reviewId: string, content: string) => {
    setReplyContents({
      ...replyContents,
      [reviewId]: content
    });
  };

  const handleSubmitReply = (reviewId: string) => {
    const replyContent = replyContents[reviewId];
    
    if (!replyContent || replyContent.trim().length < 3) {
      // Arată eroare pentru răspuns prea scurt
      return;
    }
    
    onAddReply(reviewId, replyContent);
    
    // Reset input
    setReplyContents({
      ...replyContents,
      [reviewId]: ''
    });
  };

  // Calculăm procentul pentru fiecare rating
  const totalRatings = Object.values(ratings.breakdown).reduce((sum, count) => sum + count, 0);
  const ratingPercentages = {
    5: (ratings.breakdown[5] / totalRatings) * 100,
    4: (ratings.breakdown[4] / totalRatings) * 100,
    3: (ratings.breakdown[3] / totalRatings) * 100,
    2: (ratings.breakdown[2] / totalRatings) * 100,
    1: (ratings.breakdown[1] / totalRatings) * 100
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Recenzii și feedback</h2>

      {/* Rezumat evaluare */}
      <div className="flex flex-col md:flex-row gap-8 mb-8">
        <div className="md:w-1/3 flex flex-col items-center justify-center p-4 bg-blue-50 rounded-lg">
          <div className="text-5xl font-bold text-blue-700">{ratings.rating.toFixed(1)}</div>
          <div className="flex items-center my-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <svg 
                key={star}
                className={`w-5 h-5 ${star <= Math.round(ratings.rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 20 20" 
                fill="currentColor"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <div className="text-gray-600">{ratings.userCount} evaluări</div>
        </div>

        <div className="md:w-2/3">
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((rating) => (
              <div key={rating} className="flex items-center">
                <div className="w-12 text-sm text-gray-600">
                  {rating} {rating === 1 ? 'stea' : 'stele'}
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full mx-2">
                  <div
                    className="h-3 bg-yellow-400 rounded-full"
                    style={{ width: `${ratingPercentages[rating as keyof typeof ratingPercentages]}%` }}
                  ></div>
                </div>
                <div className="w-12 text-sm text-gray-600">
                  {ratings.breakdown[rating as keyof typeof ratings.breakdown]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Formular recenzie */}
      {!userHasReviewed && (
        <div className="mb-8">
          {showReviewForm ? (
            <form onSubmit={handleSubmitReview} className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Adaugă o recenzie pentru "{bookTitle}"</h3>
              
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Evaluarea ta</label>
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => handleRatingChange(rating)}
                      className="text-2xl mr-2 focus:outline-none"
                    >
                      <svg 
                        className={`w-8 h-8 ${rating <= userRating ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 20 20" 
                        fill="currentColor"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </button>
                  ))}
                  <span className="ml-2 text-gray-600">
                    {userRating === 0 ? 'Selectează o evaluare' : `${userRating} ${userRating === 1 ? 'stea' : 'stele'}`}
                  </span>
                </div>
              </div>
              
              <div className="mb-4">
                <label htmlFor="reviewContent" className="block text-gray-700 mb-2">Recenzia ta</label>
                <textarea
                  id="reviewContent"
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ce ți-a plăcut sau nu la această carte? Împărtășește opinia ta..."
                  value={reviewContent}
                  onChange={(e) => setReviewContent(e.target.value)}
                ></textarea>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition"
                  onClick={() => setShowReviewForm(false)}
                >
                  Anulează
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                >
                  Postează recenzia
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowReviewForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Scrie o recenzie
            </button>
          )}
        </div>
      )}

      {/* Listă recenzii */}
      <div className="space-y-6">
        {reviews.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            <p>Nu există recenzii pentru această carte.</p>
            <p>Fii primul care lasă o recenzie!</p>
          </div>
        ) : (
          reviews.map((review) => (
            <div key={review.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center">
                  {review.userAvatar ? (
                    <img 
                      src={review.userAvatar} 
                      alt={review.userName} 
                      className="w-10 h-10 rounded-full mr-3"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center mr-3">
                      {review.userName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="font-medium text-gray-800">{review.userName}</div>
                    <div className="text-sm text-gray-500">{review.date}</div>
                  </div>
                </div>
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg 
                      key={star}
                      className={`w-4 h-4 ${star <= review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 20 20" 
                      fill="currentColor"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>

              <div className="mb-3 text-gray-700">
                {review.content}
              </div>

              <div className="flex items-center mt-4 mb-2 space-x-4">
                <button 
                  className={`flex items-center text-sm ${review.hasLiked ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
                  onClick={() => onLikeReview(review.id)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill={review.hasLiked ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                  </svg>
                  {review.likes} {review.likes === 1 ? 'apreciere' : 'aprecieri'}
                </button>

                <button 
                  className="flex items-center text-sm text-gray-500 hover:text-blue-600"
                  onClick={() => toggleReviewExpand(review.id)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  {review.replies?.length || 0} {review.replies?.length === 1 ? 'răspuns' : 'răspunsuri'}
                </button>
              </div>

              {expandedReviews[review.id] && (
                <div className="mt-4 pl-4 border-l-2 border-gray-200">
                  {/* Răspunsuri */}
                  {review.replies && review.replies.length > 0 ? (
                    <div className="space-y-4 mb-4">
                      {review.replies.map((reply) => (
                        <div key={reply.id} className={`p-3 rounded-md ${reply.isAuthor ? 'bg-blue-50' : 'bg-white border border-gray-100'}`}>
                          <div className="flex items-start">
                            {reply.userAvatar ? (
                              <img 
                                src={reply.userAvatar} 
                                alt={reply.userName} 
                                className="w-8 h-8 rounded-full mr-2"
                              />
                            ) : (
                              <div className={`w-8 h-8 rounded-full ${reply.isAuthor ? 'bg-blue-600' : 'bg-gray-500'} text-white flex items-center justify-center mr-2`}>
                                {reply.userName.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-center">
                                <div className="font-medium text-gray-800">
                                  {reply.userName}
                                  {reply.isAuthor && (
                                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                      Autor
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">{reply.date}</div>
                              </div>
                              <div className="text-gray-700 mt-1">
                                {reply.content}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 mb-4">
                      Nu există răspunsuri la această recenzie.
                    </div>
                  )}

                  {/* Formular răspuns */}
                  <div className="flex items-start space-x-2">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center">
                      U
                    </div>
                    <div className="flex-1">
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={2}
                        placeholder="Scrie un răspuns..."
                        value={replyContents[review.id] || ''}
                        onChange={(e) => handleReplyChange(review.id, e.target.value)}
                      ></textarea>
                      <div className="flex justify-end mt-2">
                        <button
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition"
                          onClick={() => handleSubmitReply(review.id)}
                        >
                          Răspunde
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Paginare */}
      {reviews.length > 5 && (
        <div className="mt-6 flex justify-center">
          <nav className="inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <a href="#" className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <span className="sr-only">Previous</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </a>
            <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium hover:bg-gray-50 text-blue-600 border-blue-500">1</a>
            <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">2</a>
            <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">3</a>
            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>
            <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">8</a>
            <a href="#" className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <span className="sr-only">Next</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </a>
          </nav>
        </div>
      )}
    </div>
  );
};

export default UserFeedback; 